/**
 * Ensure stack + on-demand plane build for MPR (PACS/manifest + light profile).
 */
import * as cornerstone from 'cornerstone-core'
import {
  getDicomColumns,
  getDicomInstanceNumber,
  getDicomPixelSpacing,
  getDicomRows,
  getDicomSliceDistance,
  getDicomSliceLocation,
  getDicomSliceThickness,
  getDicomSpacingBetweenSlice,
  hasDicomDataSet,
} from '../functions'
import { loadFromCacheBySop } from './loadDicomInstanceWithCache'
import { resolveStackIppEnds } from './mprImagePlane'

/**
 * Rellena columns/rows/sliceDistance/sliceLocation en un file entry.
 */
export function enrichFileGeometry(file) {
  if (!file || !file.image) return file
  const image = file.image
  if (file.columns == null) file.columns = getDicomColumns(image) || image.columns
  if (file.rows == null) file.rows = getDicomRows(image) || image.rows
  if (file.sliceDistance == null) {
    const d = getDicomSliceDistance(image)
    if (d != null) file.sliceDistance = d
  }
  if (file.sliceLocation == null) {
    const loc = getDicomSliceLocation(image)
    if (loc != null) file.sliceLocation = loc
  }
  if (file.instanceNumber == null) {
    const n = getDicomInstanceNumber(image)
    if (n != null) file.instanceNumber = parseInt(n, 10)
  }
  return file
}

async function loadFileImage(file, opts = {}) {
  const allowNetwork = opts.network !== false
  if (file && file.image && typeof file.image.getPixelData === 'function' && hasDicomDataSet(file.image)) {
    return file.image
  }
  if (file && file.sopInstanceUID) {
    const cached = await loadFromCacheBySop(file.sopInstanceUID)
    if (cached && cached.image) {
      file.image = cached.image
      if (cached.imageId) file.imageId = cached.imageId
      file.renderKind = 'dicom'
      return cached.image
    }
  }
  if (!allowNetwork) return null
  if (file && file.imageId && !/^https?:/i.test(file.imageId) && file.renderKind !== 'preview') {
    try {
      const image = await cornerstone.loadAndCacheImage(file.imageId)
      file.image = image
      return image
    } catch (e) {
      console.warn('ensureMprStackReady: load imageId failed', e)
    }
  }
  // wadouri / dicomUrl
  const url = file && (file.dicomUrl || (file.imageId && String(file.imageId).replace(/^wadouri:/i, '')))
  if (url && /^https?:/i.test(url)) {
    try {
      const imageId = /^wadouri:/i.test(String(file.imageId || ''))
        ? file.imageId
        : `wadouri:${url}`
      const image = await cornerstone.loadAndCacheImage(imageId)
      file.image = image
      file.imageId = imageId
      file.renderKind = 'dicom'
      return image
    } catch (e) {
      console.warn('ensureMprStackReady: wadouri load failed', e)
    }
  }
  return null
}

/**
 * Cortes ya decodificados (memoria o IDB). No dispara red.
 */
export async function collectReadyMprFiles(files) {
  if (!Array.isArray(files) || !files.length) return []
  const out = []
  for (let i = 0; i < files.length; i++) {
    const f = files[i]
    if (!f) continue
    const image = await loadFileImage(f, { network: false })
    if (!image || typeof image.getPixelData !== 'function') continue
    enrichFileGeometry(f)
    out.push(f)
  }
  return out
}

/**
 * Asegura que todos los cortes tengan pixelData DICOM y metadatos de geometría.
 * @param {Array} files
 * @param {{ onProgress?: (done: number, total: number) => void, signal?: { aborted?: boolean } }} [opts]
 * @returns {Promise<{ ok: boolean, files: Array, error?: string }>}
 */
export async function ensureMprStackReady(files, opts = {}) {
  if (!Array.isArray(files) || files.length === 0) {
    return { ok: false, files: [], error: 'No hay archivos en la serie.' }
  }

  const out = files.slice()
  const total = out.length
  let done = 0

  for (let i = 0; i < out.length; i++) {
    if (opts.signal && opts.signal.aborted) {
      return { ok: false, files: out, error: 'MPR cancelado.' }
    }
    const f = out[i]
    if (!f) continue
    if (f.renderKind === 'preview' && !f.dicomUrl && !f.sopInstanceUID) {
      done++
      if (opts.onProgress) opts.onProgress(done, total)
      continue
    }
    const image = await loadFileImage(f, { network: opts.allowPartial ? false : true })
    if (!image || typeof image.getPixelData !== 'function') {
      if (opts.allowPartial) {
        done++
        if (opts.onProgress) opts.onProgress(done, total)
        continue
      }
      return {
        ok: false,
        files: out,
        error: `No se pudo decodificar el corte ${i + 1}/${total} para MPR.`,
      }
    }
    enrichFileGeometry(f)
    done++
    if (opts.onProgress) opts.onProgress(done, total)
    // Yield al event loop cada pocos cortes
    if (i % 4 === 3) {
      await new Promise((r) => setTimeout(r, 0))
    }
  }

  const usable = out.filter(
    (f) => f && f.image && typeof f.image.getPixelData === 'function' && hasDicomDataSet(f.image)
  )
  if (usable.length < 3) {
    return {
      ok: false,
      files: out,
      error: 'No hay suficientes cortes DICOM decodificados para MPR.',
    }
  }

  return { ok: true, files: usable.length === out.length ? out : usable }
}

/**
 * Spacings para planos reconstruidos.
 */
export function resolveMprSpacings(image, mprData) {
  const rowSp = parseFloat(getDicomPixelSpacing(image, 0)) || image.rowPixelSpacing || 1
  const colSp = parseFloat(getDicomPixelSpacing(image, 1)) || image.columnPixelSpacing || 1
  const zSp =
    (mprData && mprData.zSpacing) ||
    parseFloat(getDicomSpacingBetweenSlice(image)) ||
    Math.abs(colSp)
  return {
    rowPixelSpacing: rowSp,
    columnPixelSpacing: colSp,
    zSpacing: zSp,
  }
}

/**
 * Extrae un plano YZ (sagittal desde axial) sin volumen isótropo interpolado.
 * indexX en coords de columna [0..cols-1]
 */
export function buildPlaneOnDemandYZ(files, indexX) {
  if (!files || !files.length) return null
  const cols = files[0].columns || files[0].image.columns
  const rows = files[0].rows || files[0].image.rows
  const zSize = files.length
  const x = Math.max(0, Math.min(cols - 1, Math.round(indexX)))
  const plane = new Int16Array(rows * zSize)
  for (let z = 0; z < zSize; z++) {
    const pd = files[z].image.getPixelData()
    for (let y = 0; y < rows; y++) {
      plane[y + rows * z] = pd[x + cols * y]
    }
  }
  return { pixelData: plane, columns: rows, rows: zSize }
}

/**
 * Extrae un plano XZ (coronal desde axial).
 * indexY en coords de fila [0..rows-1]
 */
export function buildPlaneOnDemandXZ(files, indexY) {
  if (!files || !files.length) return null
  const cols = files[0].columns || files[0].image.columns
  const rows = files[0].rows || files[0].image.rows
  const zSize = files.length
  const y = Math.max(0, Math.min(rows - 1, Math.round(indexY)))
  const plane = new Int16Array(cols * zSize)
  for (let z = 0; z < zSize; z++) {
    const pd = files[z].image.getPixelData()
    for (let x = 0; x < cols; x++) {
      plane[x + cols * z] = pd[x + cols * y]
    }
  }
  return { pixelData: plane, columns: cols, rows: zSize }
}

/**
 * LRU simple de planos on-demand.
 */
export function createPlaneLru(max = 3) {
  const map = new Map()
  return {
    get(key) {
      if (!map.has(key)) return null
      const v = map.get(key)
      map.delete(key)
      map.set(key, v)
      return v
    },
    set(key, value) {
      if (map.has(key)) map.delete(key)
      map.set(key, value)
      while (map.size > max) {
        const first = map.keys().next().value
        map.delete(first)
      }
    },
    clear() {
      map.clear()
    },
  }
}

export function computeZSpacingForVolume(files, sliceIndex = 0) {
  const image = files[sliceIndex] && files[sliceIndex].image
  if (!image) return 1
  const xPixelSpacing =
    parseFloat(getDicomPixelSpacing(image, 1)) ||
    image.columnPixelSpacing ||
    1

  // 1) Span IPP primer↔último / (n-1): cubre el volumen entero (ReferenceLines)
  if (files && files.length >= 2) {
    const ends = resolveStackIppEnds(files)
    if (ends && ends.zSpacing > 0) return ends.zSpacing
  }

  // 2) Mediana de deltas consecutivos (IPP / sliceDistance)
  const deltas = []
  if (files && files.length >= 2) {
    for (let i = 1; i < files.length; i++) {
      const a = files[i - 1]
      const b = files[i]
      let d0 = a && a.sliceDistance
      let d1 = b && b.sliceDistance
      if (d0 == null && a && a.image) d0 = getDicomSliceDistance(a.image)
      if (d1 == null && b && b.image) d1 = getDicomSliceDistance(b.image)
      if (d0 == null && a && a.sliceLocation != null) d0 = a.sliceLocation
      if (d1 == null && b && b.sliceLocation != null) d1 = b.sliceLocation
      if (d0 != null && d1 != null) {
        const d = Math.abs(Number(d1) - Number(d0))
        if (Number.isFinite(d) && d > 1e-6) deltas.push(d)
      }
    }
  }
  if (deltas.length) {
    deltas.sort((a, b) => a - b)
    return deltas[Math.floor(deltas.length / 2)]
  }

  // 2) Tags DICOM (pueden no coincidir con el paso real si hay overlap)
  const spacingBetween = getDicomSpacingBetweenSlice(image)
  if (spacingBetween && spacingBetween > 0) return spacingBetween

  const thickness = getDicomSliceThickness(image)
  if (thickness && thickness > 0) return thickness

  return xPixelSpacing
}
